import {
  applyStateDelta,
  rollDice,
  type GameModule,
  type GameTurnInput,
  type GameTurnOutcome,
  type ViewSpec,
  type ViewWidget,
} from "@roqeet/core";

/**
 * 스탯 기반 RPG — 네이티브 게임 모듈(결정 #24/#25). 텍스트 어드벤처(#26 태그
 * 프로토콜)를 한 단계 확장해 **주사위 판정**(dice 위젯#27)과 HP/MP 바·레벨/경험치를
 * 실증한다. GameModule 추상화가 장르를 넘어 동작함을 보이는 두 번째 네이티브 모듈.
 *
 * 주사위 판정 흐름(재현 가능, 결정 #4):
 *   모델이 판정 전에 `@설정 dc = 15`로 난이도를 깔고 `@주사위 d20`을
 *   내면, applyTurn이 stateDelta(=dc)를 먼저 반영한 뒤 주사위를 시드 RNG로 굴려
 *   결과를 dc와 비교해 성공/실패를 확정한다. dc는 사용 후 소거한다.
 *
 * 상태 모양(moduleState):
 *   { hp, maxHp, mp, maxMp, level, xp, xpToNext, gold, inventory: string[],
 *     dc?, lastRoll?, lastRollNotation?, lastRollSuccess?, choices, ended, ending }
 */

const DEFAULT_STATE = (): Record<string, unknown> => ({
  hp: 30,
  maxHp: 30,
  mp: 10,
  maxMp: 10,
  level: 1,
  xp: 0,
  xpToNext: 100,
  gold: 0,
  inventory: [],
});

/** 모델에 RoQeet 게임 명령 문법을 지시(결정 #26). 현재 스탯을 함께 알려 일관성을 높인다. */
function protocolInstructions(state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): string {
  const hp = num(state["hp"], 30);
  const maxHp = num(state["maxHp"], 30);
  const mp = num(state["mp"], 10);
  const maxMp = num(state["maxMp"], 10);
  const level = num(state["level"], 1);
  const gold = num(state["gold"], 0);
  const inventory = Array.isArray(state["inventory"]) ? (state["inventory"] as unknown[]) : [];
  const ended = state["ended"] === true;
  
  const tf = (k: string, fallback: string, ...args: any[]) => t ? t(k, ...args) : fallback;

  return [
    tf("game.prompt.sr.p1", "당신은 스탯 기반 RPG의 게임마스터입니다. 2인칭 시점으로 장면을 생생하게 묘사하고, 행동의 결과를 스탯에 반영하세요."),
    "본문 서술 뒤에 RoQeet 게임 명령으로 상태를 갱신하세요(필요할 때만, 정확한 문법으로).",
    "- 선택지: @선택지 { ... } 블록에 보통 2~4개를 한 줄씩 작성",
    "- 능력 판정: 판정이 필요하면 먼저 @설정 dc = 15 로 난이도(10 쉬움~20 어려움)를 정하고, 다음 줄에 @주사위 d20 을 내세요. 시스템이 주사위를 굴려 성공/실패를 판정합니다. 결과는 다음 턴에 반영해 서술하세요.",
    "- 피해/회복: @감소 hp 8 / @증가 hp 5 / @감소 mp 3",
    "- 보상: @증가 gold 20 / @증가 xp 40 (경험치가 차면 시스템이 레벨업 처리)",
    '- 아이템: @추가 inventory "강철 검"',
    "- 게임 종료(사망/클리어): @설정 ended = true 와 함께 결말을 서술",
    "플레이어 HP가 0이 되면 사망으로 게임이 끝납니다. 서사는 자유롭게, RoQeet 명령은 정확하게 쓰세요.",
    "",
    tf("game.prompt.sr.p4", "현재 상태 — 레벨: {lvl}, HP: {hp}/{maxHp}, MP: {mp}/{maxMp}, EXP: {exp}, 돈: {gold}G{ended}", 
      { lvl: level, hp, maxHp, mp, maxMp, exp: num(state["xp"], 0), gold, ended: ended ? tf("game.prompt.ta.p9", " (게임 종료됨)") : "" }),
    `소지품: [${inventory.join(", ") || tf("game.prompt.ta.p10", "없음")}]`
  ].join("\n");
}

/** 파싱된 턴을 상태에 반영(결정 #26/#28). 주사위 판정 + 레벨업을 처리. */
function applyTurn(input: GameTurnInput, t?: (k: string, ...args: any[]) => string): GameTurnOutcome {
  // 1) 상태 연산 먼저 반영(dc 설정/피해/보상 등). dc가 여기서 상태에 들어온다.
  let state = applyStateDelta(input.state, input.parsed.stateDelta);

  // 2) 주사위 판정(결정 #4 재현성). 첫 굴림만 처리해 단순화. dc가 있으면 성공 판정.
  if (input.parsed.rolls.length > 0) {
    const first = input.parsed.rolls[0]!;
    const result = rollDice(first.notation, input.random);
    const dc = state["dc"];
    const next: Record<string, unknown> = {
      ...state,
      lastRoll: result,
      lastRollNotation: first.notation,
    };
    if (typeof dc === "number") {
      next["lastRollSuccess"] = result >= dc;
      delete next["dc"]; // 1회용 난이도는 소거(다음 판정에 누수 방지).
    } else {
      delete next["lastRollSuccess"]; // 난이도 없는 굴림은 성공/실패 표시 안 함.
    }
    state = next;
  }

  // 3) 레벨업: xp가 xpToNext 이상이면 레벨을 올리고 최대치를 키운다(반복 처리).
  state = applyLevelUps(state);

  // 4) 선택지를 상태에 영속(결정 #28): viewSpec이 순수 함수가 되도록.
  state = { ...state, choices: input.parsed.choices };

  const ended = state["ended"] === true || num(state["hp"], 1) <= 0;
  const outcome: GameTurnOutcome = { state, ended };
  if (ended) outcome.endingLabel = num(state["hp"], 1) <= 0 ? (t ? t("game.endingDeath", "사망") : "사망") : (t ? t("game.endingEnd", "엔딩") : "엔딩");
  return outcome;
}

/** xp가 임계치를 넘으면 레벨업(누적 처리). HP/MP 최대치를 키우고 가득 회복. */
function applyLevelUps(state: Record<string, unknown>): Record<string, unknown> {
  let next = { ...state };
  let guard = 0;
  while (num(next["xp"], 0) >= num(next["xpToNext"], Infinity) && guard < 100) {
    guard++;
    const level = num(next["level"], 1) + 1;
    const xp = num(next["xp"], 0) - num(next["xpToNext"], 0);
    const xpToNext = Math.round(num(next["xpToNext"], 100) * 1.5);
    const maxHp = num(next["maxHp"], 30) + 10;
    const maxMp = num(next["maxMp"], 10) + 5;
    next = {
      ...next,
      level,
      xp,
      xpToNext,
      maxHp,
      maxMp,
      hp: maxHp, // 레벨업 시 완전 회복.
      mp: maxMp,
    };
  }
  return next;
}

/** 현재 상태에서 렌더할 위젯 선언(결정 #27). */
function viewSpec(state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): ViewSpec {
  const hp = num(state["hp"], 30);
  const maxHp = num(state["maxHp"], 30);
  const mp = num(state["mp"], 10);
  const maxMp = num(state["maxMp"], 10);
  const level = num(state["level"], 1);
  const xp = num(state["xp"], 0);
  const xpToNext = num(state["xpToNext"], 100);
  const gold = num(state["gold"], 0);
  const inventory = Array.isArray(state["inventory"])
    ? (state["inventory"] as unknown[]).map((x) => String(x))
    : [];
  const choices = Array.isArray(state["choices"])
    ? (state["choices"] as unknown[]).map((x) => String(x))
    : [];
  const ended = state["ended"] === true || hp <= 0;

  const hud: ViewWidget[] = [
    { kind: "stat-bar", label: "HP", value: Math.max(0, hp), max: maxHp, tone: "hp" },
    { kind: "stat-bar", label: "MP", value: Math.max(0, mp), max: maxMp, tone: "mp" },
    {
      kind: "stat-panel",
      stats: [
        { label: "Lv", value: level },
        { label: "EXP", value: `${xp}/${xpToNext}` },
        { label: "Gold", value: gold },
      ],
    },
  ];

  const widgets: ViewWidget[] = [];

  // 직전 주사위 판정 결과(있으면). 성공/실패는 dc가 있었을 때만 표시.
  if (state["lastRoll"] != null) {
    const dice: ViewWidget = {
      kind: "dice",
      notation: String(state["lastRollNotation"] ?? "d20"),
      result: num(state["lastRoll"], 0),
    };
    if (typeof state["lastRollSuccess"] === "boolean") {
      dice.success = state["lastRollSuccess"];
    }
    widgets.push(dice);
  }

  if (inventory.length > 0) {
    widgets.push({ kind: "inventory", title: t ? t("game.labelInventory", "소지품") : "소지품", items: inventory.map((name) => ({ name })) });
  }

  if (ended) {
    widgets.push({
      kind: "notice",
      tone: hp <= 0 ? "danger" : "success",
      text: hp <= 0 ? (t ? t("game.noticeDeath", "당신은 쓰러졌습니다. 게임 종료.") : "당신은 쓰러졌습니다. 게임 종료.") : (t ? t("game.noticeEnd", "모험이 끝났습니다.") : "모험이 끝났습니다."),
    });
  } else if (choices.length > 0) {
    widgets.push({ kind: "choices", choices });
  }

  return { widgets, hud };
}

export const statRpgModule: GameModule = {
  manifest: {
    id: "stat-rpg",
    title: "스탯 RPG",
    titleKey: "game.mod.statRpg.title",
    description: "주사위 판정으로 진행하는 AI RPG. HP/MP·레벨·경험치·인벤토리.",
    descriptionKey: "game.mod.statRpg.desc",
    kind: "native",
    icon: "dice",
  },
  initialState: DEFAULT_STATE,
  protocolInstructions,
  applyTurn,
  viewSpec,
};

function num(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}
